import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { collection, getDocs, setDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from './Loader';
import SuccessModal from './SuccessModal';

const CurrentTermModal = ({ isOpen, onClose, onSuccess }) => {
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [currentTerm, setCurrentTerm] = useState(null);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFirstTimeSetup, setIsFirstTimeSetup] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Check if current term exists
        const currentTermRef = doc(db, 'current-term', 'current');
        const currentTermDoc = await getDoc(currentTermRef);
        
        if (currentTermDoc.exists()) {
          setCurrentTerm(currentTermDoc.data());
          setSelectedSession(currentTermDoc.data().sessionId);
          setSelectedTerm(currentTermDoc.data().termId);
          setIsFirstTimeSetup(false);
        } else {
          setIsFirstTimeSetup(true);
        }

        // Fetch all sessions
        const sessionsSnapshot = await getDocs(collection(db, 'sessions'));
        setSessions(sessionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));

        // Fetch all terms
        const termsSnapshot = await getDocs(collection(db, 'terms'));
        setTerms(termsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));
      } catch (error) {
        setError("Error fetching term data:", error);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handleSaveCurrentTerm = async () => {
    if (!selectedSession || !selectedTerm) return;
    
    setLoading(true);
    try {
      const selectedSessionData = sessions.find(s => s.id === selectedSession);
      const selectedTermData = terms.find(t => t.id === selectedTerm);
      const currentTermRef = doc(db, 'current-term', 'current');

      const termData = {
        sessionId: selectedSession,
        termId: selectedTerm,
        sessionName: selectedSessionData?.name || '',
        termName: selectedTermData?.name || '',
        updatedAt: new Date()
      };

      if (isFirstTimeSetup) {
        // First time setup - create the document
        await setDoc(currentTermRef, termData);
      } else {
        // Update existing document
        await setDoc(currentTermRef, termData, { merge: true });
      }
      
      onSuccess?.();      
      onClose();
    } catch (error) {
      setError("Error saving current term:", error);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onRequestClose={onClose}
      className="custom-modal"
      overlayClassName="custom-modal-overlay"
    >
      <p className="size5 caps">Current Term Settings</p>
      
      {loading ? (
        <Loader text="LOADING" />
      ) : (
        <>
          {!isFirstTimeSetup && currentTerm ? (
            <p style={{ marginBottom: '20px' }}>
              Current Term <br /> <strong>{currentTerm.sessionName} - {currentTerm.termName} Term</strong>
            </p>
          ) : (
            <p style={{ marginBottom: '20px', color: '#666' }}>
              No current term set. Please select session and term.
            </p>
          )}

          <div className="form-group">
            <label>Select Session</label>
            <select
              className="input-field"
              value={selectedSession}
              onChange={(e) => setSelectedSession(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a session</option>
              {sessions.map(session => (
                <option key={session.id} value={session.id}>
                  {session.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Select Term</label>
            <select
              className="input-field"
              value={selectedTerm}
              onChange={(e) => setSelectedTerm(e.target.value)}
              disabled={loading}
            >
              <option value="">Select a term</option>
              {terms.map(term => (
                <option key={term.id} value={term.id}>
                  {term.name}
                </option>
              ))}
            </select>
          </div>

          <div className="row align-center gap-1" style={{ marginTop: '20px' }}>
            <button 
              className="login-button" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className="login-button" 
              onClick={handleSaveCurrentTerm}
              disabled={!selectedSession || !selectedTerm || loading}
            >
              {loading ? 'Saving...' : isFirstTimeSetup ? 'Set Current Term' : 'Update Term'}
            </button>
          </div>
          {showModal && (
            <SuccessModal
            icon={error ? "xmark" : "check"}
            iconColor={error ? "red" : "green"}
            message={error || successMessage}
            onClose={() => {
              setError("");
              setSuccessMessage("");
              setShowModal(false);
            }}
            />
        )}
        </>
      )}
    </Modal>
  );
};

export default CurrentTermModal;
